import { api } from "./client";

const get = async <T = any>(url: string, params?: any): Promise<T> => (await api.get(url, { params })).data;
const post = async <T = any>(url: string, body?: any): Promise<T> => (await api.post(url, body ?? {})).data;
const patch = async <T = any>(url: string, body?: any): Promise<T> => (await api.patch(url, body)).data;
const put = async <T = any>(url: string, body?: any): Promise<T> => (await api.put(url, body)).data;

export const AuthApi = {
  login: (username: string, password: string) => post("/auth/login", { username, password }),
  register: (body: any) => post("/auth/register", body),
  me: () => get("/auth/me"),
  logout: () => post("/auth/logout"),
};

export const RegApi = {
  checkUsername: (username: string) => post("/auth/register/check-username", { username }),
  validateCard: (package_qr: string, card_serial: string) => post("/auth/register/validate-card", { package_qr, card_serial }),
  sendOtp: (body: any) => post("/auth/register/send-otp", body),
  verify: (registration_id: string, otp: string) => post("/auth/register/verify", { registration_id, otp }),
  activate: (body: any) => post("/me/activate", body),
  forgot: (username: string) => post("/auth/forgot-password", { username }),
  reset: (username: string, otp: string, new_password: string) => post("/auth/reset-password", { username, otp, new_password }),
};

export const CardApi = {
  packages: () => get<any[]>("/admin/packages"),
  createPackage: (body: any) => post("/admin/packages", body),
  updatePackage: (id: string, body: any) => patch(`/admin/packages/${id}`, body),
  generate: (package_id: string, count: number) => post("/admin/cards/generate", { package_id, count }),
  cards: (params?: any) => get<any[]>("/admin/cards", params),
  voidCard: (id: string) => post(`/admin/cards/${id}/void`),
  lookup: (serial: string) => get(`/cashier/cards/${encodeURIComponent(serial)}`),
  pay: (serial: string, receipt_number: string, amount: number) => post(`/cashier/cards/${encodeURIComponent(serial)}/pay`, { receipt_number, amount }),
  recent: () => get<any[]>("/cashier/recent"),
};

export const PublicApi = {
  health: () => get("/health"),
  config: () => get("/config/public"),
  availability: (lat: number, lng: number) => get("/service/availability", { lat, lng }),
  route: (from: any, to: any) => get("/geo/route", { from_lat: from.lat, from_lng: from.lng, to_lat: to.lat, to_lng: to.lng }),
};

export const MeApi = {
  updateProfile: (body: any) => patch("/me/profile", body),
  changePassword: (current_password: string, new_password: string) => post("/me/password", { current_password, new_password }),
  notifications: () => get("/me/notifications"),
  readAll: () => post("/me/notifications/read-all"),
};

export const GeoApi = {
  search: (q: string) => get("/geo/search", { q }),
  reverse: (lat: number, lng: number) => get("/geo/reverse", { lat, lng }),
};

export const BookingApi = {
  create: (body: any) => post("/bookings", body),
  mine: () => get<any[]>("/bookings"),
  get: (id: string) => get(`/bookings/${id}`),
  quote: (body: any) => post("/bookings/quote", body),
  cancel: (id: string, reason = "") => post(`/bookings/${id}/cancel`, { reason }),
};

export const ResponderApi = {
  me: () => get("/responder/me"),
  setAvailability: (availability: "available" | "offline") => post("/responder/availability", { availability }),
  sendLocation: (p: { lat: number; lng: number; accuracy?: number }) => post("/responder/location", p),
  assignments: () => get<any[]>("/responder/assignments"),
  history: () => get<any[]>("/responder/history"),
  accept: (id: string) => post(`/responder/bookings/${id}/accept`),
  reject: (id: string, reason: string) => post(`/responder/bookings/${id}/reject`, { reason }),
  status: (id: string, status: string) => post(`/responder/bookings/${id}/status`, { status }),
};

export const DispatchApi = {
  queue: () => get<any[]>("/dispatch/queue"),
  responders: () => get<any[]>("/dispatch/responders"),
  alerts: () => get<any[]>("/dispatch/alerts"),
  history: () => get<any[]>("/dispatch/history"),
  assign: (id: string, responder_id: string) => post(`/dispatch/bookings/${id}/assign`, { responder_id }),
  priority: (id: string, priority: number) => post(`/dispatch/bookings/${id}/priority`, { priority }),
  status: (id: string, status: string, note = "") => post(`/dispatch/bookings/${id}/status`, { status, note }),
};

export const PaymentApi = {
  membership: () => get("/memberships/me"),
  joinPlan: (plan_id: string) => post("/memberships", { plan_id }),
  payBooking: (id: string) => post(`/payments/booking/${id}`),
  mine: () => get<any[]>("/payments/mine"),
  get: (id: string) => get(`/payments/${id}`),
  simulate: (id: string, outcome: "paid" | "failed") => post(`/payments/${id}/simulate`, { outcome }),
};

export const AdminApi = {
  users: (params?: any) => get<any[]>("/admin/users", params),
  createUser: (body: any) => post("/admin/users", body),
  updateUser: (id: string, body: any) => patch(`/admin/users/${id}`, body),
  resetPassword: (id: string, password: string) => post(`/admin/users/${id}/reset-password`, { password }),
  verify: (id: string, decision: string, notes: string) => post(`/admin/responders/${id}/verify`, { decision, notes }),
  setPhoto: (id: string, photo: string) => post(`/admin/users/${id}/photo`, { photo }),
  memberships: () => get<any[]>("/admin/memberships"),
  updateMembership: (id: string, status: string) => patch(`/admin/memberships/${id}`, { status }),
  vehicles: () => get<any[]>("/admin/vehicles"),
  createVehicle: (body: any) => post("/admin/vehicles", body),
  updateVehicle: (id: string, body: any) => patch(`/admin/vehicles/${id}`, body),
  config: () => get("/admin/config"),
  saveConfig: (body: any) => put("/admin/config", body),
  transactions: () => get<any[]>("/admin/transactions"),
  analytics: () => get("/admin/analytics"),
  audit: () => get<any[]>("/admin/audit"),
};
